import { useEffect, useRef, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";

interface Props {
  blocked: boolean;
  pending: boolean;
  onRetry: () => Promise<void>;
  children?: ReactNode;
}

/** Keep drafts mounted while a topmost modal owns focus, including over portals. */
export function SessionRecovery({ blocked, pending, onRetry, children }: Props) {
  const content = useRef<HTMLDivElement>(null);
  const retry = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (blocked) (pending ? content.current : retry.current)?.focus();
  }, [blocked, pending]);

  return <>
    <div className="contents" {...(blocked ? { inert: "" } : {})} aria-hidden={blocked || undefined}>
      {children}
    </div>
    <Dialog.Root open={blocked}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-app-bg" />
        <Dialog.Content
          ref={content}
          className="fixed left-1/2 top-1/2 z-[100] max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 space-y-5 overflow-y-auto rounded-2xl border border-app-border bg-app-surface p-6 text-center shadow-premium-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          aria-busy={pending}
          onOpenAutoFocus={event => {
            event.preventDefault();
            previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            (retry.current ?? content.current)?.focus();
          }}
          onCloseAutoFocus={event => {
            event.preventDefault();
            if (previousFocus.current?.isConnected) previousFocus.current.focus();
          }}
          onEscapeKeyDown={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
        >
          <div className="space-y-2" role={pending ? "status" : "alert"}>
            <Dialog.Title className="font-display text-2xl text-app-fg-strong">
              {pending ? "Verificando sua sessão" : "Não foi possível verificar sua sessão"}
            </Dialog.Title>
            <Dialog.Description className="text-sm text-app-fg">
              {pending
                ? "Aguarde enquanto confirmamos seu acesso à clínica."
                : "Seu acesso ainda não foi confirmado. Verifique sua conexão e tente novamente para continuar."}
            </Dialog.Description>
          </div>
          {!pending && <Button ref={retry} variant="petrol" className="min-h-11" onClick={() => void onRetry()}>
            Tentar novamente
          </Button>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  </>;
}
